import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import Disclaimer from "../components/Disclaimer";
import { toast } from "sonner";
import { AlertTriangle, Download, TrendingUp, RefreshCw, ShieldCheck, Sparkles } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/dashboard");
      setData(r.data);
    } catch { toast.error("Errore nel caricamento del cruscotto"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const isPlus = user?.plan === "plus";

  const exportPdf = async () => {
    try {
      const res = await api.get("/report/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "iva-radar-report.pdf"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.status === 403) toast.error("Export PDF disponibile solo con il Piano Plus");
      else toast.error("Errore nell'export PDF");
    }
  };

  if (loading || !data) return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar/>
      <div className="p-10 text-center text-[#6B6B6B]">Caricamento cruscotto…</div>
    </div>
  );

  const pc = data.periodo_corrente;
  const alertOn = pc.alert;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="label-caps">Cruscotto · {user?.sector || "settore non impostato"}</div>
            <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1E3F33] mt-2">Ciao {user?.name?.split(" ")[0] || ""}</h1>
          </div>
          <div className="flex gap-2">
            <button data-testid="dashboard-refresh" onClick={load} className="btn-lift px-4 py-2 text-sm border border-[#EBE5DF] rounded-md hover:bg-[#EBE5DF] flex items-center gap-2">
              <RefreshCw size={14}/> Aggiorna
            </button>
            <button data-testid="dashboard-export-pdf" onClick={exportPdf}
              className={`btn-lift px-4 py-2 text-sm rounded-md flex items-center gap-2 ${isPlus ? "bg-[#1E3F33] text-white hover:bg-[#152D24]" : "border border-[#EBE5DF] text-[#6B6B6B] cursor-not-allowed"}`}
              title={isPlus ? "" : "Disponibile con il Piano Plus"}>
              <Download size={14}/> Export PDF {!isPlus && "(Plus)"}
            </button>
          </div>
        </div>

        <div className="mb-6"><Disclaimer /></div>

        {/* Onboarding CTA when no movimenti yet — CSV/XML is the primary at launch */}
        {data.totale_movimenti === 0 && (
          <div data-testid="onboarding-hero" className="mb-8 card-flat p-8 bg-white border-[#1E3F33]">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
              <div className="lg:col-span-3">
                <div className="label-caps text-[#C25934]">Inizia da qui</div>
                <h2 className="font-serif-display text-3xl lg:text-4xl text-[#1E3F33] mt-2">
                  Aggiungi le tue prime fatture
                </h2>
                <p className="mt-3 text-[#2C2C2C] leading-relaxed max-w-xl">
                  Per calcolare la tua IVA stimata IVA Radar ha bisogno dei tuoi movimenti.
                  Puoi importarli in tre modi: da un file CSV, da fatture elettroniche XML SDI, o inserendoli manualmente.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a href="/movimenti" data-testid="onboarding-import-primary" className="btn-lift inline-flex items-center gap-2 px-6 py-3 bg-[#1E3F33] text-white rounded-md hover:bg-[#152D24]">
                    Importa CSV o XML SDI
                  </a>
                  <a href="/movimenti" data-testid="onboarding-manual-secondary" className="btn-lift inline-flex items-center gap-2 px-6 py-3 border border-[#1E3F33] text-[#1E3F33] rounded-md hover:bg-[#EBE5DF]">
                    Inserisci un movimento
                  </a>
                </div>
                <p className="mt-4 text-xs text-[#6B6B6B]">
                  <ShieldCheck size={12} className="inline mr-1 text-[#6B6B6B]"/>
                  <strong>Presto disponibile:</strong> accesso automatico alle fatture via SPID e delega Cassetto Fiscale, senza più caricare nulla.
                </p>
              </div>
              <div className="lg:col-span-2 hidden lg:block">
                <div className="bg-[#F9F8F6] border border-[#EBE5DF] rounded-md p-5 text-sm">
                  <div className="font-serif-display text-lg text-[#1E3F33] mb-2">Formato CSV consigliato</div>
                  <div className="font-mono text-xs text-[#6B6B6B]">
                    data,tipo,imponibile,<br/>
                    aliquota_iva,detraibilita,<br/>
                    categoria,descrizione
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Small SPID badge, discreet - visible only when has movimenti but delega not active */}
        {data.totale_movimenti > 0 && user?.delega_stato !== "attiva" && (
          <div data-testid="spid-coming-soon" className="mb-6 flex items-center justify-between gap-3 px-4 py-3 bg-[#EBE5DF]/50 border border-[#EBE5DF] rounded-md text-sm">
            <div className="flex items-center gap-2 text-[#2C2C2C]">
              <Sparkles size={14} className="text-[#C25934]"/>
              <span><strong>Presto disponibile:</strong> accesso automatico alle fatture via SPID e delega Cassetto Fiscale.</span>
            </div>
            <a href="/autorizza-fatture" data-testid="spid-preview-link" className="text-xs text-[#6B6B6B] underline hover:text-[#1E3F33] whitespace-nowrap">Anteprima</a>
          </div>
        )}

        {user?.delega_stato === "attiva" && (
          <div data-testid="delega-active-badge" className="mb-6 inline-flex items-center gap-2 px-3 py-1.5 bg-[#EBE5DF] text-[#2D5A47] text-xs rounded-full">
            <ShieldCheck size={14}/> Delega Cassetto Fiscale attiva {user.delega_attivata_at && `dal ${new Date(user.delega_attivata_at).toLocaleDateString("it-IT")}`}
          </div>
        )}

        {alertOn && (
          <div data-testid="dashboard-alert" className="mb-6 flex items-start gap-3 border border-[#C25934] bg-[#FDF3EE] px-4 py-3 rounded-md">
            <AlertTriangle className="text-[#C25934] mt-0.5" size={18}/>
            <div className="text-sm">
              <div className="font-medium text-[#A83C22]">Soglia superata</div>
              <div className="text-[#2C2C2C]">Il saldo IVA stimato ({eur(pc.saldo)}) supera la tua soglia di allerta ({eur(pc.threshold)}). Valuta di accantonare liquidità.</div>
            </div>
          </div>
        )}

        {/* North Star + KPI */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div data-testid="kpi-saldo" className="card-flat p-8 lg:col-span-1">
            <div className="label-caps">Saldo IVA stimato · periodo corrente</div>
            <div className={`font-serif-display text-6xl mt-3 ${alertOn ? "text-[#A83C22]" : pc.saldo>=0 ? "text-[#1E3F33]" : "text-[#2D5A47]"}`} data-testid="saldo-value">{eur(pc.saldo)}</div>
            {alertOn && <div className="mt-1 text-xs text-[#A83C22] uppercase tracking-widest font-medium">Sopra soglia</div>}
            <div className="mt-2 text-xs text-[#6B6B6B]">{pc.start} → {pc.end}</div>
            <div className="mt-4 text-xs text-[#6B6B6B]">Soglia personale: <span className="text-[#2C2C2C]">{eur(pc.threshold)}</span></div>
          </div>
          <div data-testid="kpi-debito" className="card-flat p-6">
            <div className="label-caps">IVA a debito (vendite)</div>
            <div className="font-serif-display text-4xl mt-3 text-[#2C2C2C]">{eur(pc.iva_debito)}</div>
          </div>
          <div data-testid="kpi-credito" className="card-flat p-6">
            <div className="label-caps">IVA a credito (acquisti)</div>
            <div className="font-serif-display text-4xl mt-3 text-[#2C2C2C]">{eur(pc.iva_credito)}</div>
          </div>
        </div>

        {/* Chart + side info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="card-flat p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif-display text-2xl text-[#1E3F33]">Storico saldo IVA (ultimi 12 mesi)</h3>
              <TrendingUp className="text-[#C25934]" size={18}/>
            </div>
            {data.storico.length === 0 ? (
              <div className="py-16 text-center text-[#6B6B6B] text-sm">Nessun movimento registrato. Importa un CSV o inserisci i primi movimenti per vedere l'andamento.</div>
            ) : (
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <LineChart data={data.storico}>
                    <CartesianGrid stroke="#EBE5DF" strokeDasharray="3 3"/>
                    <XAxis dataKey="periodo" stroke="#6B6B6B" fontSize={12}/>
                    <YAxis stroke="#6B6B6B" fontSize={12}/>
                    <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #EBE5DF" }} formatter={(v)=>eur(v)}/>
                    <ReferenceLine y={pc.threshold} stroke="#C25934" strokeDasharray="4 4" label={{ value: "Soglia", fill: "#C25934", fontSize: 11 }}/>
                    <Line type="monotone" dataKey="saldo" stroke="#1E3F33" strokeWidth={2.5} dot={{ r: 3, fill: "#C25934" }}/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <div className="card-flat p-6">
            <div className="label-caps mb-3">Piano Plus</div>
            <div className="space-y-4">
              <div>
                <div className="text-xs text-[#6B6B6B]">Proiezione trimestrale</div>
                <div className="font-serif-display text-2xl text-[#1E3F33]">{eur(data.proiezione_trimestrale)}</div>
              </div>
              <div>
                <div className="text-xs text-[#6B6B6B]">Stesso mese anno precedente</div>
                <div className="font-serif-display text-2xl text-[#1E3F33]">
                  {data.confronto_anno_precedente == null ? "—" : eur(data.confronto_anno_precedente)}
                </div>
              </div>
              {!isPlus && (
                <div className="text-xs text-[#6B6B6B] pt-3 border-t border-[#EBE5DF]">
                  Dati completi disponibili con il <span className="text-[#1E3F33] font-medium">Piano Plus</span>.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Airtable liquidazioni (source of truth se configurato) */}
        {data.airtable_configured && data.airtable_liquidazioni && data.airtable_liquidazioni.length > 0 && (
          <div className="card-flat p-6 mb-6" data-testid="airtable-liquidazioni">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-caps text-[#C25934]">Da Airtable · Motore Calcolo</div>
                <h3 className="font-serif-display text-2xl text-[#1E3F33] mt-1">Liquidazioni ufficiali del periodo</h3>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#6B6B6B]">
                    <th className="py-2 pr-3">Periodo</th>
                    <th className="py-2 pr-3">Data calcolo</th>
                    <th className="py-2 pr-3 text-right">IVA a Debito</th>
                    <th className="py-2 pr-3 text-right">IVA a Credito</th>
                    <th className="py-2 pr-3 text-right">Saldo Stimato</th>
                    <th className="py-2 pr-3">Alert</th>
                  </tr>
                </thead>
                <tbody>
                  {data.airtable_liquidazioni.slice().reverse().map((l, i) => (
                    <tr key={i} className="border-t border-[#EBE5DF]">
                      <td className="py-2 pr-3 font-medium">{l.periodo}</td>
                      <td className="py-2 pr-3 text-[#6B6B6B]">{l.data_calcolo || "—"}</td>
                      <td className="py-2 pr-3 text-right">{eur(l.iva_debito)}</td>
                      <td className="py-2 pr-3 text-right">{eur(l.iva_credito)}</td>
                      <td className="py-2 pr-3 text-right font-medium">{eur(l.saldo)}</td>
                      <td className="py-2 pr-3">{l.sopra_soglia && <span className="text-[#C25934] text-xs">↑ sopra soglia</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Movimenti recenti */}
        <div className="card-flat p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif-display text-2xl text-[#1E3F33]">Movimenti recenti</h3>
            <a href="/movimenti" data-testid="link-all-movimenti" className="text-sm text-[#1E3F33] underline">Vedi tutti</a>
          </div>
          {data.movimenti_recenti.length === 0 ? (
            <div className="py-8 text-center text-[#6B6B6B] text-sm">Nessun movimento ancora.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#6B6B6B]">
                    <th className="py-2 pr-3">Data</th>
                    <th className="py-2 pr-3">Tipo</th>
                    <th className="py-2 pr-3">Categoria</th>
                    <th className="py-2 pr-3 text-right">Imponibile</th>
                    <th className="py-2 pr-3 text-right">Aliquota</th>
                    <th className="py-2 pr-3 text-right">Detr. %</th>
                    <th className="py-2 pr-3 text-right">IVA</th>
                  </tr>
                </thead>
                <tbody>
                  {data.movimenti_recenti.map((m) => (
                    <tr key={m.id} className="border-t border-[#EBE5DF]">
                      <td className="py-2 pr-3">{m.data}</td>
                      <td className="py-2 pr-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${m.tipo==="vendita" ? "bg-[#EBE5DF] text-[#1E3F33]" : "bg-[#FDF3EE] text-[#C25934]"}`}>{m.tipo}</span>
                      </td>
                      <td className="py-2 pr-3">{m.categoria}</td>
                      <td className="py-2 pr-3 text-right">{eur(m.imponibile)}</td>
                      <td className="py-2 pr-3 text-right">{m.aliquota_iva}%</td>
                      <td className="py-2 pr-3 text-right">{m.detraibilita}%</td>
                      <td className="py-2 pr-3 text-right font-medium">{eur(m.tipo==="vendita" ? m.iva_a_debito : m.iva_detraibile)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
